import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { aboutInfo } from '@/mocks/articles';

const ABOUT_HERO_IMAGE = "https://readdy.ai/api/search-image?query=A%20warm%20minimal%20abstract%20team%20collaboration%20concept%20with%20soft%20geometric%20shapes%20and%20gentle%20gradients%20clean%20professional%20editorial%20style%20warm%20amber%20cream%20and%20teal%20tones%20high%20detail&width=1800&height=600&seq=about-hero-safety&orientation=landscape";

export default function About() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 安全指南',
    description: aboutInfo.introduction,
    url: 'https://safety-guide.example.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      description: aboutInfo.tagline,
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 成人网站安全使用完全指南"
        description="安全指南致力于推广网络安全意识和数字素养，为成年用户提供专业、可靠的内容安全指导。了解我们的品牌故事、使命和联系方式。"
        keywords="成人网站安全指南,关于我们,网络安全教育,成人内容安全指导,隐私保护指南"
        canonicalUrl="https://safety-guide.example.com/about"
        ogImage={ABOUT_HERO_IMAGE}
        schemaJson={aboutSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
            <img
              src={ABOUT_HERO_IMAGE}
              alt="关于我们"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30" />
            <div className="relative z-10 w-full text-center px-4 md:px-6 pt-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto leading-relaxed">
                致力于推广网络安全意识和数字素养的独立信息平台
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <div className="mb-8">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: '关于我们' },
                  ]}
                />
              </div>

              <div className="mb-14">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">
                  品牌介绍
                </h2>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-12 h-12 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg font-bold text-lg">
                      安
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground-900">
                        {aboutInfo.brandName}
                      </h3>
                      <p className="text-sm text-foreground-600">{aboutInfo.tagline}</p>
                    </div>
                  </div>
                  <p className="text-base text-foreground-700 leading-loose mb-5">
                    {aboutInfo.introduction}
                  </p>
                  <p className="text-base text-foreground-700 leading-loose">
                    {aboutInfo.mission}
                  </p>
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">
                  我们的价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aboutInfo.values.map((v, i) => (
                    <div
                      key={i}
                      className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex items-start gap-4"
                    >
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-check-double-line text-lg" />
                      </span>
                      <div>
                        <h3 className="font-heading font-semibold text-base text-foreground-900 mb-1.5">
                          {v.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {v.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">
                  信誉与承诺
                </h2>
                <div className="bg-accent-50 rounded-lg border border-accent-200/60 p-6 md:p-8">
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                        <i className="ri-shield-check-line" />
                      </span>
                      <div>
                        <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">信息准确性</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">我们严格审核每篇文章的内容，确保提供的信息准确、及时、基于可靠来源。所有内容在发布前均经过专业审查流程。</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                        <i className="ri-eye-off-line" />
                      </span>
                      <div>
                        <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">隐私保护</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">本网站不收集用户个人信息，不使用追踪Cookie，不要求用户注册。我们以身作则，践行所倡导的隐私保护理念。</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                        <i className="ri-refresh-line" />
                      </span>
                      <div>
                        <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">持续更新</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">网络安全形势和技术日新月异，我们承诺定期审查和更新所有内容，确保读者获取最新的安全信息和最佳实践。</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                  <div className="flex flex-col sm:flex-row gap-8">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-mail-line text-lg" />
                        </span>
                        <div>
                          <p className="font-medium text-sm text-foreground-900">电子邮件</p>
                          <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.response}</p>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                          <i className="ri-question-answer-line text-lg" />
                        </span>
                        <div>
                          <p className="font-medium text-sm text-foreground-900">反馈与建议</p>
                          <p className="text-sm text-foreground-600">我们重视每一位读者的声音</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-500">如有内容建议、纠错或其他反馈，欢迎通过邮件与我们联系。我们认真对待每一条反馈。</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}